// Portal yetki modeli v2 (Dalga 3, spec 2026-10-23 §8.1) — erişim çekmecesi taslağı (SAF).
// Portal › Kullanıcılar çekmecesi ve danışman › Müşteri profili › "KPI erişimi" editörü AYNI
// modeli kullanır: set başına rol + üç durumlu kategori haritası + gösterge istisnaları + yaprak
// işletme kapsamı. Sunucu sözleşmesi: packages/shared/src/schemas/client-admin.ts (KpiAccessGrant),
// birleştirme/kapsam kuralları backend services/client-admin-scope.ts (Y5/Y5a).
//
// Kilit kuralı (Y5a/6): hedefin bir boyutu NULL (tam) ve Admin o boyutta kısıtlıysa alan Admin için
// salt-okunurdur. `categoriesAllowed` şemada zorunlu anahtar olduğundan kilitli kategori alanı
// `null` ("değişiklik yok") gönderilir; `entitiesAllowed` opsiyonel olduğundan kilitliyken anahtar
// HİÇ yazılmaz (sunucu korur). Kısıtlı Admin `role` göndermez (sunucu değişmemiş rolde de 403 verir).

import {
  categoryLevelFor,
  type ClientAdminScope,
  type ClientAdminSet,
  type ClientAdminSetCategory,
  type ClientEntity,
  type ClientUserSetAccess,
  type KpiAccessGrant,
  type KpiAccessRole,
  type KpiCategoryAccessMap,
  type KpiEffectiveClientAccess,
} from "@mahir/shared";
import { CLIENT_ADMIN_COPY } from "./client-admin-copy";
import { visibleCategories } from "./client-kpi-access-ui";

export interface SetDraftV2 {
  setId: string;
  role: KpiAccessRole;
  /** `true` = NULL harita (tüm kategoriler). */
  allCategories: boolean;
  /** metricId → seviye; anahtar yok = Gizli. `allCategories` iken kullanılmaz. */
  categoryAccess: KpiCategoryAccessMap;
  /** setIndicatorId → seviye (kategori seviyesinin yerine geçer). */
  overrides: Map<string, KpiEffectiveClientAccess>;
  /** `true` = NULL (tüm işletmeler). */
  allEntities: boolean;
  /** Yaprak işletme id'leri; `allEntities` iken kullanılmaz. */
  entities: Set<string>;
}

/** setId → set taslağı; anahtar yoksa sete erişim yok. */
export type AccessDraftV2 = Map<string, SetDraftV2>;

const RANK: Record<KpiEffectiveClientAccess, number> = { closed: 0, readonly: 1, open: 2 };

export function minLevel(
  a: KpiEffectiveClientAccess,
  b: KpiEffectiveClientAccess,
): KpiEffectiveClientAccess {
  return RANK[a] <= RANK[b] ? a : b;
}

// ─── Taslak kurulumu ─────────────────────────────────────────────────────────

export function draftFromAccessV2(a: ClientUserSetAccess): SetDraftV2 {
  return {
    setId: a.setId,
    role: a.role,
    allCategories: a.categoryAccess === null,
    categoryAccess: a.categoryAccess === null ? {} : { ...a.categoryAccess },
    overrides: new Map(a.overrides.map((o) => [o.setIndicatorId, o.access])),
    allEntities: a.entitiesAllowed === null,
    entities: new Set(a.entitiesAllowed ?? []),
  };
}

export function draftMapFromAccessV2(list: ClientUserSetAccess[]): AccessDraftV2 {
  return new Map(list.map((a) => [a.setId, draftFromAccessV2(a)]));
}

/**
 * Yeni set kutusu açılınca varsayılan: veri sahibi + tam erişim. Kısıtlı Admin NULL veremez
 * (Y5a/6-8): kategori = setin (kırpılmış) kategorilerinin tamamı `open`, işletme = DTO'daki yapraklar.
 */
export function newSetDraftV2(
  set: ClientAdminSet,
  scope: ClientAdminScope | null,
  entities: ClientEntity[],
): SetDraftV2 {
  const catsScoped = scope?.categories != null;
  const entsScoped = scope?.entities != null;
  const categoryAccess: KpiCategoryAccessMap = catsScoped ? openRootCategories(set) : {};
  return {
    setId: set.id,
    role: "owner",
    allCategories: !catsScoped,
    categoryAccess,
    overrides: new Map(),
    allEntities: !entsScoped,
    entities: entsScoped ? new Set(leafEntityIds(entities)) : new Set(),
  };
}

export function toggleSetInDraftV2(
  d: AccessDraftV2,
  setId: string,
  on: boolean,
  make: () => SetDraftV2,
): AccessDraftV2 {
  const next = new Map(d);
  if (on) {
    if (!next.has(setId)) next.set(setId, make());
  } else next.delete(setId);
  return next;
}

export function patchSetDraft(
  d: AccessDraftV2,
  setId: string,
  patch: (s: SetDraftV2) => SetDraftV2,
): AccessDraftV2 {
  const cur = d.get(setId);
  if (!cur) return d;
  const next = new Map(d);
  next.set(setId, patch(cur));
  return next;
}

// ─── Kilitler (kısıtlı Admin) ────────────────────────────────────────────────

/** Kısıtlı Admin: kapsamı en az bir boyutta daraltılmış (Süper Admin / danışman / tam Admin değil). */
export function isScopedAdmin(scope: ClientAdminScope | null | undefined): boolean {
  return !!scope && (scope.categories !== null || scope.entities !== null);
}

export interface AccessLocks {
  /** Hedefin kategori alanı NULL (tam) ve Admin bu boyutta kısıtlı → salt-okunur. */
  lockedNullCategories: boolean;
  lockedNullEntities: boolean;
  /** Kısıtlı Admin "Tüm kategoriler/işletmeler"i (NULL) SEÇEMEZ. */
  noAllCategories: boolean;
  noAllEntities: boolean;
  /** Rolü yalnız iki boyutta da tam kapsamlı Admin değiştirir; kısıtlıda `role` hiç gönderilmez. */
  lockedRole: boolean;
}

export function accessLocks(
  original: Pick<ClientUserSetAccess, "categoryAccess" | "entitiesAllowed"> | undefined,
  scope: ClientAdminScope | null | undefined,
): AccessLocks {
  const noAllCategories = scope?.categories != null;
  const noAllEntities = scope?.entities != null;
  return {
    lockedNullCategories: original?.categoryAccess === null && noAllCategories,
    lockedNullEntities: original?.entitiesAllowed === null && noAllEntities,
    noAllCategories,
    noAllEntities,
    lockedRole: isScopedAdmin(scope),
  };
}

// ─── Grant üretimi ───────────────────────────────────────────────────────────

export function draftToGrantV2(
  d: SetDraftV2,
  opts: {
    lockedNullCategories: boolean;
    lockedNullEntities: boolean;
    /** Kısıtlı Admin: gövdede `role` yok. */
    omitRole?: boolean;
    /** İstisnalar yüklenenle aynıysa gönderilmez (sunucu korur; kategoriye dönüşmüş düğüm 422 vermez). */
    omitOverrides?: boolean;
  },
): KpiAccessGrant {
  const grant: KpiAccessGrant = {
    setId: d.setId,
    categoriesAllowed:
      opts.lockedNullCategories || d.allCategories ? null : { ...d.categoryAccess },
  };
  if (!opts.lockedNullEntities) {
    grant.entitiesAllowed = d.allEntities ? null : [...d.entities];
  }
  if (!opts.omitOverrides) {
    grant.overrides = [...d.overrides].map(([setIndicatorId, access]) => ({
      setIndicatorId,
      access,
    }));
  }
  if (!opts.omitRole) grant.role = d.role;
  return grant;
}

/** Taslaktaki istisnalar yüklenen erişimle aynı mı (sıra önemsiz). */
export function overridesUnchanged(
  d: SetDraftV2,
  original: ClientUserSetAccess | undefined,
): boolean {
  const base = new Map((original?.overrides ?? []).map((o) => [o.setIndicatorId, o.access]));
  if (base.size !== d.overrides.size) return false;
  for (const [id, access] of d.overrides) if (base.get(id) !== access) return false;
  return true;
}

/** Tüm taslak → grant listesi (kilitler her set için orijinal satırdan hesaplanır). */
export function draftsToGrants(
  drafts: AccessDraftV2,
  originals: ReadonlyMap<string, ClientUserSetAccess>,
  scope: ClientAdminScope | null | undefined,
): KpiAccessGrant[] {
  return [...drafts.values()].map((d) => {
    const original = originals.get(d.setId);
    const locks = accessLocks(original, scope);
    return draftToGrantV2(d, {
      lockedNullCategories: locks.lockedNullCategories,
      lockedNullEntities: locks.lockedNullEntities,
      omitRole: locks.lockedRole,
      omitOverrides: original !== undefined && overridesUnchanged(d, original),
    });
  });
}

/**
 * İyimser kilit (`expected`): yazılan ya da silinen HER set için okunan jeton; satır yoktu → `null`.
 * Admin'de kapsam dışı setler DTO'da hiç yok → dokunulmaz (sunucu da denetlemez).
 */
export function buildExpected(
  grants: readonly KpiAccessGrant[],
  originals: ReadonlyMap<string, ClientUserSetAccess>,
): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const id of new Set([...grants.map((g) => g.setId), ...originals.keys()])) {
    out[id] = originals.get(id)?.accessToken ?? null;
  }
  return out;
}

// ─── Kategori / istisna mantığı ──────────────────────────────────────────────

/** Kategori düğümü (indicatorId) için taslaktaki kategori seviyesi (`k`). */
export function categoryLevelOf(
  set: ClientAdminSet,
  d: SetDraftV2,
  indicatorId: string,
): KpiEffectiveClientAccess {
  return categoryLevelFor(set.indicators, d.allCategories ? null : d.categoryAccess, indicatorId);
}

function consultantCeiling(set: ClientAdminSet, indicatorId: string): KpiEffectiveClientAccess {
  return set.indicators.find((i) => i.id === indicatorId)?.consultantAccess ?? "open";
}

/** Ekranda gösterilen seviye: min(istisna ?? kategori, danışman tavanı) — rol tavanı ayrıca uygulanır. */
export function effectiveLevel(
  set: ClientAdminSet,
  d: SetDraftV2,
  indicatorId: string,
): KpiEffectiveClientAccess {
  const own = d.overrides.get(indicatorId) ?? categoryLevelOf(set, d, indicatorId);
  return minLevel(own, consultantCeiling(set, indicatorId));
}

/** Haritayı değiştirir; `closed` anahtarı SİLER (girdisi olmayan düğüm gizlidir). */
export function setCategoryLevel(
  d: SetDraftV2,
  metricId: string,
  level: KpiEffectiveClientAccess,
): SetDraftV2 {
  const categoryAccess = { ...d.categoryAccess };
  if (level === "closed") delete categoryAccess[metricId];
  else categoryAccess[metricId] = level;
  return { ...d, categoryAccess };
}

/**
 * Görünür kategorilerin KÖKLERİ `open` — NULL haritayla eşdeğer başlangıç (alt kategoriler anahtarsız
 * üstten miras alır: üst gizlenince altı da gizlenir; alt kendi seviyesini ayrıca seçebilir).
 */
function openRootCategories(set: ClientAdminSet): KpiCategoryAccessMap {
  const visible = visibleCategories(set);
  const ids = new Set(visible.map((c) => c.indicatorId));
  const map: KpiCategoryAccessMap = {};
  for (const c of visible) {
    const parent = set.indicators.find((i) => i.id === c.indicatorId)?.parentId ?? null;
    if (parent === null || !ids.has(parent)) map[c.metricId] = "open";
  }
  return map;
}

/** "Tüm kategoriler" kapatılınca kök kategoriler `open` (NULL ile eşdeğer başlangıç). */
export function disableAllCategories(set: ClientAdminSet, d: SetDraftV2): SetDraftV2 {
  return { ...d, allCategories: false, categoryAccess: openRootCategories(set) };
}

export function setOverride(
  d: SetDraftV2,
  setIndicatorId: string,
  access: KpiEffectiveClientAccess,
): SetDraftV2 {
  const overrides = new Map(d.overrides);
  overrides.set(setIndicatorId, access);
  return { ...d, overrides };
}

export function removeOverride(d: SetDraftV2, setIndicatorId: string): SetDraftV2 {
  const overrides = new Map(d.overrides);
  overrides.delete(setIndicatorId);
  return { ...d, overrides };
}

/** İstisna kategori seviyesiyle (danışman tavanı dahil) aynı sonucu veriyor → "etkisiz". */
export function overrideIsNoop(d: SetDraftV2, set: ClientAdminSet, indicatorId: string): boolean {
  const o = d.overrides.get(indicatorId);
  if (o === undefined) return false;
  const c = consultantCeiling(set, indicatorId);
  return minLevel(o, c) === minLevel(categoryLevelOf(set, d, indicatorId), c);
}

/** İstisnalı düğüm sonradan çocuk kazandı (yalnız kendi satırına uygulanır — §4.1). */
export function becameCategoryWarning(set: ClientAdminSet, indicatorId: string): boolean {
  return set.indicators.find((i) => i.id === indicatorId)?.hasChildren === true;
}

/**
 * Bu gösterge taslakta KAPALIYSA ve ondan hesaplanan açık göstergeler varsa onların etiketleri
 * (§4.1 — engel değil, uyarı). Gösterge açıksa boş.
 */
export function derivedOpenDependents(
  set: ClientAdminSet,
  d: SetDraftV2,
  indicatorId: string,
): string[] {
  if (effectiveLevel(set, d, indicatorId) !== "closed") return [];
  return set.indicators
    .filter(
      (i) =>
        i.isDerived &&
        i.derivedInputIds.includes(indicatorId) &&
        effectiveLevel(set, d, i.id) !== "closed",
    )
    .map((i) => i.label);
}

/** İstisna eklenebilecek göstergeler: çocuksuz, danışmanın kapatmadığı. */
export function overrideCandidates(
  set: ClientAdminSet,
  d: SetDraftV2,
): { id: string; label: string; path: string }[] {
  const byId = new Map(set.indicators.map((i) => [i.id, i]));
  const pathOf = (id: string): string => {
    const names: string[] = [];
    const seen = new Set<string>();
    let cur = byId.get(id)?.parentId ?? null;
    while (cur && !seen.has(cur)) {
      seen.add(cur);
      const p = byId.get(cur);
      if (!p) break;
      names.unshift(p.label);
      cur = p.parentId;
    }
    return names.join(CLIENT_ADMIN_COPY.drawer.overrides.pathSep);
  };
  return set.indicators
    .filter((i) => !i.hasChildren && i.consultantAccess !== "closed" && !d.overrides.has(i.id))
    .map((i) => ({ id: i.id, label: i.label, path: pathOf(i.id) }));
}

// ─── İşletmeler ──────────────────────────────────────────────────────────────

/** Gruba bağlı (kendisi dahil değil) tüm YAPRAK işletme id'leri; yaprak verilirse kendisi. */
export function entityLeafIdsUnder(entities: ClientEntity[], groupId: string): string[] {
  const kids = new Map<string, ClientEntity[]>();
  for (const e of entities) {
    if (e.parentId) kids.set(e.parentId, [...(kids.get(e.parentId) ?? []), e]);
  }
  const out: string[] = [];
  const seen = new Set<string>();
  const type = new Map(entities.map((e) => [e.id, e.type]));
  const walk = (id: string): void => {
    if (seen.has(id)) return;
    seen.add(id);
    const children = kids.get(id) ?? [];
    // Çocuksuz GRUP yaprak değildir (sunucu 422 entity_not_leaf).
    if (children.length === 0) {
      if (type.get(id) !== "group") out.push(id);
    } else for (const c of children) walk(c.id);
  };
  walk(groupId);
  return out;
}

/** Tüm yaprak (çocuksuz, grup olmayan) işletme id'leri. */
export function leafEntityIds(entities: ClientEntity[]): string[] {
  const parents = new Set(entities.map((e) => e.parentId).filter((p): p is string => !!p));
  return entities.filter((e) => e.type !== "group" && !parents.has(e.id)).map((e) => e.id);
}

/**
 * Kayıt sonrası kullanıcı HİÇ işletmesiz kalır mı? Admin'in kendi kapsamındaki seçimi boşsa bile
 * kullanıcının kapsam dışı işletmeleri (sunucu birleştirmede korur) varsa boş değildir — daraltma
 * serbesttir. Kilitli NULL alan (tam) boş sayılmaz.
 */
export function entitiesWouldBeEmpty(
  d: SetDraftV2,
  original: Pick<ClientUserSetAccess, "hiddenEntityCount"> | undefined,
  lockedNull: boolean,
): boolean {
  if (lockedNull || d.allEntities || d.entities.size > 0) return false;
  return (original?.hiddenEntityCount ?? 0) === 0;
}

export function toggleEntities(d: SetDraftV2, ids: readonly string[], on: boolean): SetDraftV2 {
  const entities = new Set(d.entities);
  for (const id of ids) {
    if (on) entities.add(id);
    else entities.delete(id);
  }
  return { ...d, entities };
}

// ─── Özet ────────────────────────────────────────────────────────────────────

export interface AccessCounts {
  edits: number;
  views: number;
  overrides: number;
  /** `null` = tüm işletmeler. */
  entities: number | null;
}

/** "düzenler 3 · görüntüler 2 · istisna 4 · işletme 2/5" — sıfır olan parça yazılmaz. */
export function formatAccessSummary(c: AccessCounts, entityTotal: number): string {
  const S = CLIENT_ADMIN_COPY.drawer.summary;
  const parts: string[] = [];
  if (c.edits > 0) parts.push(`${S.edits} ${c.edits.toString()}`);
  if (c.views > 0) parts.push(`${S.views} ${c.views.toString()}`);
  if (parts.length === 0) parts.push(CLIENT_ADMIN_COPY.access.noneOpen);
  if (c.overrides > 0) parts.push(`${S.overrides} ${c.overrides.toString()}`);
  if (c.entities !== null)
    parts.push(`${S.entities} ${c.entities.toString()}/${entityTotal.toString()}`);
  return parts.join(" · ");
}

export function accessSummaryV2(d: SetDraftV2, set: ClientAdminSet, entityTotal: number): string {
  let edits = 0;
  let views = 0;
  for (const cat of visibleCategories(set)) {
    let lv = minLevel(categoryLevelOf(set, d, cat.indicatorId), cat.consultantAccess);
    if (d.role === "auditor" && lv === "open") lv = "readonly";
    if (lv === "open") edits += 1;
    else if (lv === "readonly") views += 1;
  }
  return formatAccessSummary(
    {
      edits,
      views,
      overrides: d.overrides.size,
      entities: d.allEntities ? null : d.entities.size,
    },
    entityTotal,
  );
}

// ─── Yönetici kapsamı özeti ──────────────────────────────────────────────────

/** Kapsam kategori id'leri (metricId) → etiketler; bilinmeyen id gösterilmez. */
export function scopeSummaryParts(
  scope: ClientAdminScope | null,
  sets: readonly ClientAdminSet[],
  entities: readonly ClientEntity[],
): { cats: string; ents: string } {
  const A = CLIENT_ADMIN_COPY.admins;
  const catLabel = new Map<string, string>();
  for (const s of sets) for (const c of s.categories) catLabel.set(c.metricId, c.label);
  const entLabel = new Map(entities.map((e) => [e.id, e.name]));
  const names = (ids: string[], dict: Map<string, string>): string => {
    const known = ids.map((id) => dict.get(id)).filter((n): n is string => !!n);
    return known.length > 0 ? known.join(", ") : ids.length.toString();
  };
  return {
    cats: scope?.categories == null ? A.scopeAllCategories : names(scope.categories, catLabel),
    ents: scope?.entities == null ? A.scopeAllEntities : names(scope.entities, entLabel),
  };
}

/**
 * Yönetici ata/düzenle gövdesindeki `scope`. Değiştirilemeyen boyut (set yok / tek işletme) mevcut
 * değerini korur; düzenlemede hiçbir boyut değişmiyorsa `undefined` → PATCH'te anahtar yok = kapsam KORUNUR.
 */
export function buildAdminScope(a: {
  role: "super_admin" | "admin";
  edit: boolean;
  categoriesEditable: boolean;
  entitiesEditable: boolean;
  allCats: boolean;
  cats: ReadonlySet<string>;
  allEnts: boolean;
  ents: ReadonlySet<string>;
  current: ClientAdminScope | null | undefined;
}): ClientAdminScope | undefined {
  if (a.role !== "admin") return undefined;
  if (a.edit && !a.categoriesEditable && !a.entitiesEditable) return undefined;
  return {
    categories: a.categoriesEditable
      ? a.allCats
        ? null
        : [...a.cats]
      : (a.current?.categories ?? null),
    entities: a.entitiesEditable ? (a.allEnts ? null : [...a.ents]) : (a.current?.entities ?? null),
  };
}

/** Danışman kartı rol varsayılanı (R2-15): firmada aktif Süper Admin yoksa Süper, varsa Admin. */
export function defaultAdminRole(
  rows: readonly { role: "super_admin" | "admin"; isActive: boolean }[],
): "super_admin" | "admin" {
  return rows.some((r) => r.role === "super_admin" && r.isActive) ? "admin" : "super_admin";
}

/** Kapsam kutusu için firma setlerinin kategori birleşimi (metricId tekil, danışman-kapalı hariç). */
export function scopeCategoryOptions(
  sets: readonly ClientAdminSet[],
): Pick<ClientAdminSetCategory, "metricId" | "label">[] {
  const seen = new Map<string, string>();
  for (const s of sets) {
    for (const c of visibleCategories(s)) if (!seen.has(c.metricId)) seen.set(c.metricId, c.label);
  }
  return [...seen].map(([metricId, label]) => ({ metricId, label }));
}
